import { useState, useEffect, useMemo } from 'react'
import { apiClient } from '@/services/api'
import { useSessionStore } from '@stores'
import { getItemImageUrl, getPlaceholderImageUrl, setFallbackImageUrl } from '@utils/image'
import {
  getItemFulfillmentStatus,
  getItemFulfillmentStatusClass,
  getItemStatus,
  getItemStatusClass,
  isRecurring,
  showFulfillment,
} from '@utils/status'
import {
  FulfillmentStatus,
  FulfillmentStatusStrings,
  InputType,
  ItemStatus,
  TranResponseStrings,
  TranResponseBadgeClass,
} from '@/constants'
import { getShortHumanReadableDate, getShortHumanReadableUTCDate } from '@utils/date'
import { formatCurrency } from '@utils/helpers'
import Modal from '@/components/ui/Modal'
import { BottomSheet } from '@/components/ui/BottomSheet'
import { useIsCompact } from '@/hooks/useDevice'
import { TrackingSidebar } from '@/components/Sidebar/TrackingSidebar'
import MemberInfo from '@/components/MemberInfo'

interface OrderItemStatusProps {
  item: any
  onEdit?: (input: any, item: any) => void
}

// Mirrors the real OrderItemStatus card structure 1:1 — same wrapper, same image
// column dimensions, same details column padding — so the skeleton occupies the
// exact same space and no layout shift happens when the API resolves.
const ItemDetailSkeleton = () => (
  <div className="w-full mx-auto mb-4">
    <div
      className="order-item-status-card rounded-xl overflow-hidden flex flex-col sm:flex-row"
      style={{
        border: '1px solid var(--border-color, #e5e7eb)',
        backgroundColor: 'var(--content-background-color)',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}>
      <div
        className="order-item-status-media flex items-center justify-center p-3 sm:p-4 sm:w-[280px] shrink-0 self-stretch"
        style={{ backgroundColor: 'var(--content-background-color)' }}>
        <div className="w-[260px] h-[260px] max-w-full rounded-xl skeleton" />
      </div>

      <div className="flex-1 py-3 px-4 sm:py-4 sm:px-5">
        <div className="h-6 w-2/3 rounded skeleton mb-2" />
        <div className="flex flex-col">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="flex justify-between items-center py-0.5 gap-4">
              <div className="h-4 w-24 shrink-0 rounded skeleton" />
              <div className="h-4 w-28 shrink-0 rounded skeleton" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
)

const OrderItemStatus = ({ item: initialItem, onEdit }: OrderItemStatusProps) => {
  const [orderItem, setOrderItem] = useState<any>(initialItem)
  const [loading, setLoading] = useState(true)
  const [showTracking, setShowTracking] = useState(false)
  const [showFulfillmentsModal, setShowFulfillmentsModal] = useState(false)
  const [showTransactions, setShowTransactions] = useState(false)
  const [showMembership, setShowMembership] = useState(false)
  const isCompact = useIsCompact()

  const session = useSessionStore(s => s.session)
  const subscriptionStatusLabels = useSessionStore(s => s.subscriptionStatusLabels)()

  useEffect(() => {
    const id = initialItem.id
    setLoading(true)

    const loadItem = async () => {
      try {
        const res = await apiClient.get(`/order_items/${id}/`)
        const fetched = res.data || {}
        setOrderItem({
          ...fetched,
          show_fulfillments: fetched.show_fulfillments ?? true,
          show_transactions: fetched.show_transactions ?? true,
        })
      } catch {
        /* best-effort */
      } finally {
        setLoading(false)
      }
    }
    loadItem()
  }, [initialItem.id])

  const findAdditionalInput = (type: number) => {
    const additionalInputs = (session as any)?.additional_inputs || []
    return additionalInputs.find((i: any) => i.type === type) || null
  }

  const cancelInput = useMemo(() => findAdditionalInput(InputType.Cancel), [session])
  const changeIntervalInput = useMemo(() => findAdditionalInput(InputType.ChangeInterval), [session])
  const changeQuantityInput = useMemo(() => findAdditionalInput(InputType.ChangeQuantity), [session])
  const billingInput = useMemo(() => findAdditionalInput(InputType.Billing), [session])

  const isItemStatusOkForInput = (input: any): boolean => {
    if (!input) {
      return false
    }
    if (input.item_statuses) {
      return input.item_statuses.indexOf(orderItem?.funnel_item_status) >= 0
    }
    return true
  }

  const canChangeBillingInterval = orderItem?.can_change_billing_interval && isItemStatusOkForInput(cancelInput)
  const canChangeQuantity = orderItem?.can_change_quantity && isItemStatusOkForInput(cancelInput)
  const canUpdateBilling = orderItem?.is_active && isItemStatusOkForInput(billingInput)

  const editItem = (input: any) => {
    if (input && onEdit) {
      onEdit(input, orderItem)
    }
  }

  const imageUrl = useMemo(() => getItemImageUrl(orderItem, 'medium'), [orderItem])
  const currency = orderItem?.order?.currency || 'USD'
  const fulfillment = orderItem?.fulfillments?.[0]
  const canTrack = !!fulfillment?.can_track
  const hasFulfillments = orderItem?.show_fulfillments && orderItem?.fulfillments?.length > 0
  const hasTransactions = orderItem?.show_transactions && orderItem?.transactions?.length > 0
  const showTrackingRow =
    !!fulfillment &&
    (canTrack ||
      [
        FulfillmentStatus.Hold,
        FulfillmentStatus.PendingShipment,
        FulfillmentStatus.Processing,
        FulfillmentStatus.Frozen,
      ].includes(Number(orderItem?.fulfillment_status)))
  const showRMANumber =
    !!fulfillment &&
    (fulfillment.status === FulfillmentStatus.RMAPending || fulfillment.status === FulfillmentStatus.Returned) &&
    !!fulfillment.rma_number?.length
  const isSubscriptionPauseScheduled = orderItem?.status !== ItemStatus.Paused && !!orderItem?.pause_resume_date
  const isSubscriptionCancelScheduled = !!orderItem?.cancel_date
  const showNextBillPrice = !!orderItem?.campaign_product?.show_next_bill_price
  const hasClub = !!(orderItem?.club_id || orderItem?.order?.club_id)
  const showNextCycleProduct =
    !!orderItem?.next_cycle_product && !!orderItem?.product && orderItem.next_cycle_product.id !== orderItem.product.id
  const showNextVariant =
    !!orderItem?.next_variant && !!orderItem?.variant && orderItem.next_variant.id !== orderItem.variant.id

  if (loading) {
    return <ItemDetailSkeleton />
  }

  const rows: { label: string; value: React.ReactNode }[] = []

  rows.push({ label: 'Order #', value: orderItem.order?.display_order_id || orderItem.order?.order_id })

  if (orderItem.order?.display_order_id && orderItem.order.display_order_id !== orderItem.order.order_id) {
    rows.push({ label: 'Order ID', value: orderItem.order.order_id })
  }

  rows.push({
    label: 'Quantity',
    value: canChangeQuantity ? (
      <button
        type="button"
        className="btn-bare font-medium underline underline-offset-2 hover:opacity-80 transition-opacity"
        style={{ color: 'var(--theme-color)' }}
        onClick={() => editItem(changeQuantityInput)}>
        {orderItem.quantity}
      </button>
    ) : (
      orderItem.quantity
    ),
  })

  if (orderItem.variant?.options) {
    const toTitleCase = (str: string) =>
      String(str || '')
        .toLowerCase()
        .replace(/\w\S*/g, w => w[0].toUpperCase() + w.slice(1))
    orderItem.variant.options.forEach((opt: any) => {
      if (!opt.is_deleted) {
        rows.push({ label: toTitleCase(opt.name), value: opt.value })
      }
    })
  }

  rows.push({ label: 'Price', value: formatCurrency(orderItem.price, currency) })

  if (orderItem.shipping_price > 0) {
    rows.push({ label: 'Shipping', value: formatCurrency(orderItem.shipping_price, currency) })
  }

  if (orderItem.amount_refunded > 0) {
    rows.push({
      label: 'Refunded',
      value: <span className="text-red-500 font-medium">{formatCurrency(orderItem.amount_refunded, currency)}</span>,
    })
  }

  if (isRecurring(orderItem)) {
    rows.push({
      label: 'Subscription',
      value: (
        <span className={`order-badge ${getItemStatusClass(orderItem)}`}>
          {getItemStatus(orderItem, subscriptionStatusLabels, false)}
        </span>
      ),
    })

    if (showNextBillPrice) {
      if (orderItem.can_cancel_subscription) {
        if (orderItem.next_bill_date) {
          rows.push({
            label: 'Next Billing',
            value: (
              <span>
                {formatCurrency(orderItem.next_cycle_payment_due || 0, currency)} on{' '}
                {canChangeBillingInterval ? (
                  <button
                    type="button"
                    className="btn-bare font-medium underline underline-offset-2 hover:opacity-80 transition-opacity"
                    style={{ color: 'var(--theme-color)' }}
                    onClick={() => editItem(changeIntervalInput)}>
                    {getShortHumanReadableUTCDate(orderItem.next_bill_date)}
                  </button>
                ) : (
                  getShortHumanReadableUTCDate(orderItem.next_bill_date)
                )}
              </span>
            ),
          })
        }
        if (isSubscriptionPauseScheduled) {
          rows.push({
            label: 'Paused until',
            value: getShortHumanReadableUTCDate(orderItem.pause_resume_date),
          })
        }
      } else if (isSubscriptionCancelScheduled) {
        rows.push({
          label: 'Cancels on',
          value: getShortHumanReadableUTCDate(orderItem.cancel_date),
        })
      }
    }
  }

  if (showFulfillment(orderItem)) {
    const statusCode = Number(fulfillment?.status ?? orderItem?.fulfillment_status)
    let dateSuffix = ''
    if (statusCode === FulfillmentStatus.Shipped && fulfillment?.ship_date) {
      dateSuffix = ' ' + getShortHumanReadableDate(fulfillment.ship_date)
    } else if (statusCode === FulfillmentStatus.Delivered && fulfillment?.delivery_date) {
      dateSuffix = ' ' + getShortHumanReadableDate(fulfillment.delivery_date)
    } else if (statusCode === FulfillmentStatus.Returned && fulfillment?.return_date) {
      dateSuffix = ' ' + getShortHumanReadableDate(fulfillment.return_date)
    }
    rows.push({
      label: 'Shipment',
      value: (
        <span className={`order-badge ${getItemFulfillmentStatusClass(orderItem)}`}>
          {getItemFulfillmentStatus(orderItem)}
          {dateSuffix}
        </span>
      ),
    })
  }

  if (fulfillment) {
    if (showTrackingRow) {
      rows.push({
        label: 'Tracking #',
        value: canTrack ? (
          <button
            type="button"
            className="btn-bare font-medium underline underline-offset-2 hover:opacity-80 transition-opacity"
            style={{ color: 'var(--theme-color)' }}
            onClick={() => setShowTracking(true)}>
            {fulfillment.tracking_number}
          </button>
        ) : (
          'Awaiting Scan'
        ),
      })
    }

    if (showRMANumber) {
      rows.push({ label: 'RMA #', value: fulfillment.rma_number })
    }
  }

  if (hasFulfillments) {
    rows.push({
      label: 'Shipments',
      value: (
        <button
          type="button"
          className="btn-bare font-medium underline underline-offset-2 hover:opacity-80 transition-opacity"
          style={{ color: 'var(--theme-color)' }}
          onClick={() => setShowFulfillmentsModal(true)}>
          View Shipments
        </button>
      ),
    })
  }

  if (hasTransactions) {
    rows.push({
      label: 'Transactions',
      value: (
        <button
          type="button"
          className="btn-bare font-medium underline underline-offset-2 hover:opacity-80 transition-opacity"
          style={{ color: 'var(--theme-color)' }}
          onClick={() => setShowTransactions(true)}>
          View Transactions
        </button>
      ),
    })
  }

  if (hasClub) {
    rows.push({
      label: 'Membership',
      value: (
        <button
          type="button"
          className="btn-bare font-medium underline underline-offset-2 hover:opacity-80 transition-opacity"
          style={{ color: 'var(--theme-color)' }}
          onClick={() => setShowMembership(true)}>
          View Membership
        </button>
      ),
    })
  }

  if (orderItem.bundled_variants?.length) {
    orderItem.bundled_variants.forEach((bundled: any) => {
      const variantLabel = bundled.variant?.options?.map((o: any) => `${o.name}: ${o.value}`).join(', ')
      rows.push({ label: variantLabel || 'Variant', value: bundled.quantity })
    })
  }

  if (showNextCycleProduct || showNextVariant) {
    rows.push({
      label: 'Next Cycle',
      value: (
        <span className="flex flex-col items-end gap-1">
          {showNextCycleProduct && (
            <span className="flex items-center gap-2">
              <img
                src={getItemImageUrl(orderItem, 'small', true)}
                alt=""
                className="w-8 h-8 rounded object-contain shrink-0"
                style={{ border: '1px solid var(--border-color, #e5e7eb)' }}
                onError={e => setFallbackImageUrl(e.nativeEvent, orderItem, 'small', true, true)}
              />
              <span dangerouslySetInnerHTML={{ __html: orderItem.next_cycle_product.name }} />
            </span>
          )}
          {showNextVariant &&
            orderItem.next_variant.options?.map(
              (opt: any) =>
                !opt.is_deleted && (
                  <span key={opt.id}>
                    {opt.name}: {opt.value}
                  </span>
                )
            )}
        </span>
      ),
    })
  }

  const nameNode = canUpdateBilling ? (
    <button
      type="button"
      className="btn-bare font-semibold text-left"
      style={{ color: 'var(--theme-color)' }}
      onClick={() => editItem(billingInput)}>
      <span dangerouslySetInnerHTML={{ __html: orderItem.name }} />
    </button>
  ) : (
    <span dangerouslySetInnerHTML={{ __html: orderItem.name }} />
  )

  return (
    <>
      <div className="w-full mx-auto mb-4">
        <div
          className="order-item-status-card rounded-xl overflow-hidden flex flex-col sm:flex-row"
          style={{
            border: '1px solid var(--border-color, #e5e7eb)',
            backgroundColor: 'var(--content-background-color)',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          }}>
          <div
            className="order-item-status-media flex items-center justify-center p-3 sm:p-4 sm:w-[280px] shrink-0 self-stretch"
            style={{ backgroundColor: 'var(--content-background-color)' }}>
            <img
              src={imageUrl}
              alt={orderItem.name}
              className="w-[260px] h-[260px] max-w-full object-contain rounded-xl"
              onError={e => {
                ;(e.target as HTMLImageElement).src = getPlaceholderImageUrl('small')
              }}
            />
          </div>

          <div className="flex-1 py-3 px-4 sm:py-4 sm:px-5">
            <h3 className="text-fluid-lg font-semibold mb-2 leading-snug" style={{ color: 'var(--text-color)' }}>
              {nameNode}
            </h3>
            <div className="flex flex-col">
              {rows.map((row, i) => (
                <div
                  key={i}
                  className="text-fluid-sm flex justify-between items-center py-0.5"
                  style={{ color: 'var(--light-text-color)' }}>
                  <span>{row.label}</span>
                  <span className="text-right" style={{ color: 'var(--text-color)' }}>
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showTracking &&
        (isCompact ? (
          <BottomSheet isOpen onClose={() => setShowTracking(false)} title="Tracking Information">
            <TrackingSidebar item={orderItem} onClear={() => setShowTracking(false)} embedded />
          </BottomSheet>
        ) : (
          <Modal isOpen onClose={() => setShowTracking(false)} title="Tracking Information" size="2xl">
            <TrackingSidebar item={orderItem} onClear={() => setShowTracking(false)} embedded />
          </Modal>
        ))}

      {showTransactions &&
        (isCompact ? (
          <BottomSheet isOpen onClose={() => setShowTransactions(false)} title="Transactions">
            <TransactionsList transactions={orderItem.transactions || []} currency={currency} />
          </BottomSheet>
        ) : (
          <Modal isOpen onClose={() => setShowTransactions(false)} title="Transactions" size="xl">
            <TransactionsList transactions={orderItem.transactions || []} currency={currency} />
          </Modal>
        ))}

      {showFulfillmentsModal &&
        (isCompact ? (
          <BottomSheet isOpen onClose={() => setShowFulfillmentsModal(false)} title="Shipments">
            <FulfillmentsList fulfillments={orderItem.fulfillments} />
          </BottomSheet>
        ) : (
          <Modal isOpen onClose={() => setShowFulfillmentsModal(false)} title="Shipments" size="lg">
            <FulfillmentsList fulfillments={orderItem.fulfillments} />
          </Modal>
        ))}

      {showMembership && <MemberInfo item={orderItem} isOpen onClose={() => setShowMembership(false)} />}
    </>
  )
}

const FulfillmentsList = ({ fulfillments }: { fulfillments: any[] }) => {
  const getDate = (f: any) => {
    if (f.delivery_date) {
      return getShortHumanReadableDate(f.delivery_date)
    }
    if (f.ship_date) {
      return getShortHumanReadableDate(f.ship_date)
    }
    if (f.created) {
      return getShortHumanReadableDate(f.created)
    }
    return '—'
  }

  const getStatus = (f: any) => FulfillmentStatusStrings[f.status] || String(f.status ?? '').replace(/_/g, ' ')

  const getRma = (f: any) =>
    f.status === FulfillmentStatus.RMAPending || f.status === FulfillmentStatus.Returned ? f.rma_number || '' : ''

  return (
    <div className="p-2 overflow-x-auto">
      <table className="w-full text-left" style={{ color: 'var(--text-color)' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--border-color, #e5e7eb)' }}>
            <th className="py-2 px-3 text-sm font-semibold" style={{ color: 'var(--light-text-color)' }}>
              Date
            </th>
            <th className="py-2 px-3 text-sm font-semibold" style={{ color: 'var(--light-text-color)' }}>
              Status
            </th>
            <th
              className="py-2 px-3 text-sm font-semibold hidden md:table-cell"
              style={{ color: 'var(--light-text-color)' }}>
              Carrier
            </th>
            <th
              className="py-2 px-3 text-sm font-semibold hidden lg:table-cell"
              style={{ color: 'var(--light-text-color)' }}>
              Method
            </th>
            <th
              className="py-2 px-3 text-sm font-semibold hidden md:table-cell"
              style={{ color: 'var(--light-text-color)' }}>
              Tracking #
            </th>
            <th
              className="py-2 px-3 text-sm font-semibold hidden md:table-cell"
              style={{ color: 'var(--light-text-color)' }}>
              RMA #
            </th>
          </tr>
        </thead>
        <tbody>
          {fulfillments.map((f: any, i: number) => (
            <tr
              key={f.id || i}
              style={{
                borderBottom: '1px solid var(--border-color, #e5e7eb)',
                backgroundColor:
                  i % 2 === 1 ? 'color-mix(in srgb, var(--text-color) 3%, var(--content-background-color))' : undefined,
              }}>
              <td className="py-2.5 px-3 text-sm">{getDate(f)}</td>
              <td className="py-2.5 px-3 text-sm">{getStatus(f)}</td>
              <td className="py-2.5 px-3 text-sm hidden md:table-cell">{f.ship_carrier || '—'}</td>
              <td className="py-2.5 px-3 text-sm hidden lg:table-cell">{f.ship_method || '—'}</td>
              <td className="py-2.5 px-3 text-sm hidden md:table-cell">{f.tracking_number || '—'}</td>
              <td className="py-2.5 px-3 text-sm hidden md:table-cell">{getRma(f) || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const TransactionsList = ({ transactions, currency }: { transactions: any[]; currency: string }) => {
  const hasRefunds = transactions.some((t: any) => Number(t.refund_amount) > 0)

  return (
    <div className="p-2 overflow-x-auto">
      {transactions.length === 0 ? (
        <p className="text-center text-base" style={{ color: 'var(--light-text-color)' }}>
          No transactions found
        </p>
      ) : (
        <table className="w-full text-left" style={{ color: 'var(--text-color)' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border-color, #e5e7eb)' }}>
              <th className="py-2 px-3 text-sm font-semibold" style={{ color: 'var(--light-text-color)' }}>
                Date
              </th>
              <th className="py-2 px-3 text-sm font-semibold" style={{ color: 'var(--light-text-color)' }}>
                Status
              </th>
              <th className="py-2 px-3 text-sm font-semibold" style={{ color: 'var(--light-text-color)' }}>
                Amount
              </th>
              {hasRefunds && (
                <th className="py-2 px-3 text-sm font-semibold" style={{ color: 'var(--light-text-color)' }}>
                  Refund
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {transactions.map((t: any, i: number) => (
              <tr
                key={t.id || i}
                style={{
                  borderBottom: '1px solid var(--border-color, #e5e7eb)',
                  backgroundColor:
                    i % 2 === 1
                      ? 'color-mix(in srgb, var(--text-color) 3%, var(--content-background-color))'
                      : undefined,
                }}>
                <td className="py-2.5 px-3 text-sm">{t.created ? getShortHumanReadableDate(t.created) : '—'}</td>
                <td className="py-2.5 px-3 text-sm">
                  <span className={`order-badge ${TranResponseBadgeClass[t.response_type] || ''}`}>
                    {TranResponseStrings[t.response_type] || 'Unknown'}
                  </span>
                </td>
                <td className="py-2.5 px-3 text-sm">{formatCurrency(t.amount, currency)}</td>
                {hasRefunds && (
                  <td className="py-2.5 px-3 text-sm">
                    {Number(t.refund_amount) > 0 ? (
                      <span className="text-red-500">{formatCurrency(t.refund_amount, currency)}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export default OrderItemStatus
